<template>
  <div id="dashboard-left">
    <div v-if="myDashboardData == null" class="dashboard-left__loading">
      <MyLoader />
    </div>

    <template v-else>
      <section class="dashboard-card asset-card" aria-labelledby="total-asset-title">
        <div class="asset-card__top">
          <div>
            <p id="total-asset-title" class="card-label">총 자산</p>
            <p class="asset-total">
              <LoadingText :is-loading="amountLoading || userInvestmentLoading">
                {{ totalAsset | commaFormat }}
              </LoadingText><span>원</span>
            </p>
          </div>
          <div v-if="fundaClassImages.length" class="asset-tier" aria-label="투자자 등급">
            <img
              v-for="fc in fundaClassImages"
              :key="fc.fundaClass.className"
              :src="'//' + fc.fundaClass.image.bucketName + '/' + fc.fundaClass.image.fileKey"
              :alt="fc.fundaClass.className"
            />
          </div>
        </div>

        <div class="asset-rows">
          <div class="metric-row">
            <span class="metric-label">투자 가능 금액</span>
            <strong class="metric-value metric-value--primary">
              <LoadingText :is-loading="amountLoading">{{ withdrawBalance | commaFormat }}</LoadingText><em>원</em>
            </strong>
          </div>
          <div class="metric-row">
            <span class="metric-label">현재 투자 중 금액</span>
            <strong class="metric-value">
              <LoadingText :is-loading="userInvestmentLoading">{{ investingPrincipal | commaFormat }}</LoadingText><em>원</em>
            </strong>
          </div>
        </div>
      </section>

      <section class="dashboard-card investment-summary" aria-labelledby="investment-summary-title">
        <h2 id="investment-summary-title" class="dashboard-card__title">투자 요약</h2>
        <p v-if="summaryLoadFailed" class="summary-load-error">
          일부 요약 데이터를 불러오지 못했습니다.
        </p>
        <div class="metric-row">
          <span class="metric-label">누적 투자건수</span>
          <strong class="metric-value"><LoadingText :is-loading="summaryLoading">{{ totalInvestmentCount | commaFormat }}</LoadingText><em>건</em></strong>
        </div>
        <div class="metric-row">
          <span class="metric-label">누적 투자 금액</span>
          <strong class="metric-value"><LoadingText :is-loading="summaryLoading">{{ totalInvestmentAmount | commaFormat }}</LoadingText><em>원</em></strong>
        </div>
        <div class="metric-row">
          <span class="metric-label">누적 입금액</span>
          <strong class="metric-value"><LoadingText :is-loading="summaryLoading">{{ totalDepositAmount | commaFormat }}</LoadingText><em>원</em></strong>
        </div>
        <div class="metric-row">
          <span class="metric-label">누적 출금액</span>
          <strong class="metric-value"><LoadingText :is-loading="summaryLoading">{{ totalWithdrawalAmount | commaFormat }}</LoadingText><em>원</em></strong>
        </div>
        <div class="profit-row">
          <span>누적 수익금</span>
          <strong><LoadingText :is-loading="summaryLoading">{{ totalEarning | commaFormat }}</LoadingText><em>원</em></strong>
        </div>
      </section>

      <section class="dashboard-card limit-card" aria-label="투자 한도">
        <InvestmentLimitText :fetch-user-investment="false" />
      </section>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { AuthTypes } from '@/store/types/auth'
import { InvestmentTypes } from '@/store/types/investment'
import MyLoader from '@/components/MyLoader.vue'
import LoadingText from '@/components/common/text/LoadingText.vue'
import InvestmentLimitText from '@/components/common/text/InvestmentLimitText.vue'

export default {
  props: {
    myDashboardData: {
      type: Object,
      default: null,
    },
  },
  components: {
    MyLoader,
    LoadingText,
    InvestmentLimitText,
  },
  data() {
    return {
      myFundaClasses: [],
      isSummaryLoading: false,
      summaryLoadedUserIdx: null,
      summaryLoadFailed: false,
      totalInvestmentAmount: 0,
      totalDepositAmount: 0,
      totalWithdrawalAmount: 0,
      totalEarning: 0,
    }
  },
  computed: {
    ...mapGetters('investment', {
      userInvestmentLoading: InvestmentTypes.getters.GET_USER_INVESTMENT_LOADING,
      userInvestment: InvestmentTypes.getters.GET_USER_INVESTMENT,
      userCountLoading: InvestmentTypes.getters.GET_USER_CNT_LOADING,
      userCount: InvestmentTypes.getters.GET_USER_CNT,
    }),
    ...mapGetters('auth', {
      userIdx: AuthTypes.getters.GET_USER_IDX,
      balance: AuthTypes.getters.GET_BALANCE,
      getBalanceLoading: AuthTypes.getters.GET_BALANCE_LOADING,
    }),
    amountLoading() {
      return this.getBalanceLoading === true
    },
    withdrawBalance() {
      return this.balance ? this.balance.withdraw : 0
    },
    fundaClassImages() {
      return this.myFundaClasses.filter(
        fc => fc.fundaClass && fc.fundaClass.image
      )
    },
    investingPrincipal() {
      return this.userInvestment ? this.userInvestment.pure : 0
    },
    totalAsset() {
      return this.withdrawBalance + this.investingPrincipal
    },
    totalInvestmentCount() {
      return this.userCount ? this.userCount.cntTotal : 0
    },
    summaryLoading() {
      return this.isSummaryLoading || this.userCountLoading
    },
  },
  watch: {
    myDashboardData: {
      immediate: true,
      handler() {
        this.setByMyDashBoardData()
      },
    },
    userIdx: {
      immediate: true,
      handler(userIdx) {
        if (userIdx) {
          this.loadInvestmentSummary()
        }
      },
    },
  },
  created() {
    if (!this.balance) {
      this.getBalance()
    }
    if (!this.userInvestment) {
      this.getUserInvestment()
    }
    this.getMyCnt()
  },
  methods: {
    ...mapActions('auth', {
      getBalance: AuthTypes.actions.GET_BALANCE,
    }),
    ...mapActions('investment', {
      getUserInvestment: InvestmentTypes.actions.GET_USER_INVESTMENT,
      getMyCnt: InvestmentTypes.actions.GET_USER_CNT,
    }),
    async loadInvestmentSummary() {
      const userIdx = this.userIdx
      if (
        !userIdx ||
        this.isSummaryLoading ||
        this.summaryLoadedUserIdx === userIdx
      ) {
        return
      }

      this.isSummaryLoading = true
      this.summaryLoadFailed = false
      try {
        const q = `
          investmentTotal: getUserTotalInvestmentAmount(userIdx: ${userIdx})
          interestTotal: getUserTotalInterestAmount(userIdx: ${userIdx})
          deposits: getUserDepositContent(userIdx: ${userIdx}) {
            gubun
            amount
          }
        `
        const data = await this.$fundaApi.query(gql`{${q}}`)
        const deposits = data.deposits || []
        const deposit = deposits.find(item => item.gubun === '입금')
        const withdrawal = deposits.find(item => item.gubun === '출금')

        this.totalInvestmentAmount = data.investmentTotal || 0
        this.totalEarning = data.interestTotal || 0
        this.totalDepositAmount = deposit ? deposit.amount : 0
        this.totalWithdrawalAmount = withdrawal
          ? Math.abs(withdrawal.amount)
          : 0
        this.summaryLoadedUserIdx = userIdx
      } catch (e) {
        this.totalInvestmentAmount = 0
        this.totalEarning = 0
        this.totalDepositAmount = 0
        this.totalWithdrawalAmount = 0
        this.summaryLoadFailed = true
      } finally {
        this.isSummaryLoading = false
      }
    },
    setByMyDashBoardData() {
      if (!this.myDashboardData) {
        return
      }

      this.myFundaClasses = this.myDashboardData.user.userFundaClasses || []
    },
  },
}
</script>

<style lang="scss" scoped>
#dashboard-left {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.dashboard-left__loading {
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #e6eaef;
  border-radius: 14px;
}

.dashboard-card {
  padding: 22px;
  background: #fff;
  border: 1px solid #e6eaef;
  border-radius: 14px;
}

.asset-card__top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.card-label,
.metric-label {
  margin: 0;
  color: #5a6675;
  font-size: 13px;
  font-weight: 500;
}

.asset-total {
  margin: 5px 0 19px;
  color: #00ade5;
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.045em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.asset-total span {
  margin-left: 3px;
  font-size: 16px;
  font-weight: 600;
}

.asset-tier {
  display: flex;
  align-items: flex-start;
  gap: 4px;
}

.asset-tier img {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.asset-rows {
  border-top: 1px dashed #dfe6ed;
}

.metric-row {
  min-height: 54px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid #f0f3f6;
}

.metric-value {
  color: #1a2330;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.02em;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.metric-value--primary {
  color: #009bd2;
}

.metric-value em {
  margin-left: 2px;
  color: #697587;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
}

.dashboard-card__title {
  margin: 0 0 6px;
  color: #1a2330;
  font-size: 15px;
  font-weight: 700;
}

.summary-load-error {
  margin: -2px 0 8px;
  color: #e16a63;
  font-size: 12px;
}

.investment-summary .metric-row:last-of-type {
  border-bottom: 0;
}

.profit-row {
  min-height: 52px;
  margin-top: 12px;
  padding: 0 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #eaf7fd;
  border-radius: 10px;
  color: #0090c4;
  font-size: 14px;
  font-weight: 600;
}

.profit-row strong {
  font-size: 17px;
  font-variant-numeric: tabular-nums;
}

.profit-row em {
  margin-left: 2px;
  color: #0090c4;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
}

.limit-card ::v-deep .left-key {
  color: #8d98a6;
  font-size: 12.5px;
}

.limit-card ::v-deep .right-value {
  color: #5a6675;
  font-size: 13px;
}

@media screen and (max-width: 959px) {
  #dashboard-left {
    gap: 16px;
  }

  .dashboard-card {
    padding: 20px;
    border-radius: 12px;
  }

  .asset-total {
    font-size: 28px;
  }

  .metric-row {
    min-height: 50px;
  }
}
</style>
